import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ApiService } from './api.service';

interface FeedbackRequest {
  model_type?: string;
  reference_id?: string;
  customer_name?: string;
  job_address?: string;
  status?: string;
  rating?: number | string;
  comments?: string;
  service_category?: string;
  testimonial_permission?: number | string | boolean;
}

interface ApiResponse<T> {
  success?: boolean;
  message?: string;
  data?: T;
}

interface FeedbackLoadData {
  feedback: FeedbackRequest;
  google_review_url?: string;
}

interface FeedbackSubmitData {
  google_review_url?: string;
  redirect_url?: string | null;
}

@Component({
  selector: 'app-customer-feedback',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <main class="min-h-screen bg-[#F7FAFC] flex items-center justify-center p-4">
      <section class="w-full max-w-2xl bg-white rounded-2xl border border-[#E7EEF5] shadow-xl overflow-hidden">
        <div class="px-6 py-5 text-white" style="background:linear-gradient(135deg,#0C3A5A,#1A6FA6);">
          <p class="text-[10px] uppercase tracking-[0.18em] font-bold text-white/70">NZ Plumbing</p>
          <h1 class="text-2xl font-semibold mt-1">Customer Feedback</h1>
          <p class="text-sm text-white/75 mt-2">Thank you for taking a moment to share your experience.</p>
        </div>

        <div class="p-6">
          @if (isLoading()) {
            <div class="py-12 text-center text-sm text-slate-500">Loading feedback request...</div>
          } @else if (errorMessage()) {
            <div class="rounded-xl border border-rose-100 bg-rose-50 p-4 text-sm text-rose-700">{{ errorMessage() }}</div>
          } @else if (submitted()) {
            <div class="py-8 text-center">
              <div class="w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 mx-auto flex items-center justify-center mb-4">
                <i class="ti ti-check text-3xl"></i>
              </div>
              <h2 class="text-xl font-semibold text-slate-900">Thank you</h2>
              <p class="text-sm text-slate-500 mt-2">Your feedback has been submitted to NZ Plumbing.</p>
              @if (googleReviewUrl()) {
                <a [href]="googleReviewUrl()" target="_blank" rel="noopener noreferrer"
                   class="mt-5 inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[#1A6FA6] px-5 text-sm font-semibold text-white shadow-sm">
                  <i class="ti ti-brand-google"></i>
                  Share your experience on Google
                </a>
              }
            </div>
          } @else {
            @if (feedback(); as item) {
              <div class="rounded-xl bg-slate-50 border border-slate-100 p-4 mb-5">
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Reference</p>
                    <p class="text-sm font-semibold text-slate-900">{{ item.model_type }} {{ item.reference_id }}</p>
                  </div>
                  <div>
                    <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Customer</p>
                    <p class="text-sm font-semibold text-slate-900">{{ item.customer_name || 'Customer' }}</p>
                  </div>
                  <div class="sm:col-span-2">
                    <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Site</p>
                    <p class="text-sm font-semibold text-slate-900">{{ item.job_address || 'Not recorded' }}</p>
                  </div>
                </div>
              </div>

              @if (alreadySubmitted()) {
                <div class="rounded-xl border border-emerald-100 bg-emerald-50 p-4 text-sm text-emerald-700">
                  Feedback has already been submitted for this request.
                </div>
              } @else {
                <form (ngSubmit)="submitFeedback()" class="space-y-5">
                  <div>
                    <p class="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Overall Rating</p>
                    <div class="grid grid-cols-5 gap-2">
                      @for (value of ratings; track value) {
                        <button type="button" (click)="selectRating(value)" [disabled]="isSaving()"
                                [attr.aria-label]="value + (value === 1 ? ' star' : ' stars')" [attr.aria-pressed]="rating() === value"
                                class="h-12 rounded-xl border text-xl font-black transition-colors"
                                [class.bg-[#1A6FA6]]="rating() >= value"
                                [class.text-white]="rating() >= value"
                                [class.border-[#1A6FA6]]="rating() >= value"
                                [class.bg-white]="rating() < value"
                                [class.text-slate-600]="rating() < value"
                                [class.border-slate-200]="rating() < value">
                          <i class="ti ti-star-filled"></i>
                          <span class="sr-only">{{ value }} {{ value === 1 ? 'star' : 'stars' }}</span>
                        </button>
                      }
                    </div>
                    <p class="text-xs text-slate-400 mt-2">{{ ratingLabel() }}</p>
                  </div>

                  @if (rating() > 0 && rating() <= 3) {
                    <div class="rounded-xl border border-amber-100 bg-amber-50 p-4 text-sm text-amber-900">
                      We’re sorry your experience did not meet expectations. Please tell us what happened so our team can follow up.
                    </div>

                    <label class="block">
                      <span class="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">What could we improve? *</span>
                      <textarea [(ngModel)]="comments" name="comments" rows="5" class="w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm focus:outline-none focus:border-[#2B9AD2]" placeholder="Please explain what happened and how we could improve."></textarea>
                    </label>

                    <label class="block">
                      <span class="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Area of service</span>
                      <select [(ngModel)]="serviceCategory" name="serviceCategory" class="w-full h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:border-[#2B9AD2]">
                        <option value="">Select category</option>
                        <option value="Communication">Communication</option>
                        <option value="Work Quality">Work Quality</option>
                        <option value="Timeliness">Timeliness</option>
                        <option value="Cleanliness">Cleanliness</option>
                        <option value="Overall Service">Overall Service</option>
                      </select>
                    </label>

                    @if (formError()) {
                      <p class="text-sm font-semibold text-rose-600">{{ formError() }}</p>
                    }

                    <button type="submit" [disabled]="isSaving() || comments.trim().length < 5" class="w-full h-11 rounded-xl text-white text-sm font-semibold shadow-sm disabled:opacity-60" style="background:linear-gradient(135deg,#2B9AD2,#1A6FA6);">
                      {{ isSaving() ? 'Submitting...' : 'Send Feedback to NZ Plumbing' }}
                    </button>

                    @if (googleReviewUrl()) {
                      <div class="border-t border-slate-100 pt-4 text-center">
                        <p class="text-xs text-slate-500 mb-3">You can also share your experience publicly on Google.</p>
                        <a [href]="googleReviewUrl()" target="_blank" rel="noopener noreferrer" class="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700">
                          <i class="ti ti-brand-google"></i>
                          Review NZ Plumbing on Google
                        </a>
                      </div>
                    }
                  } @else if (rating() >= 4) {
                    <div class="rounded-xl border border-sky-100 bg-sky-50 p-4 text-center">
                      <i class="ti ti-loader-2 animate-spin text-2xl text-[#1A6FA6]"></i>
                      <p class="mt-2 text-sm font-semibold text-sky-900">Opening NZ Plumbing on Google…</p>
                    </div>
                    @if (formError()) {
                      <p class="text-sm font-semibold text-rose-600">{{ formError() }}</p>
                    }
                  }
                </form>
              }
            }
          }
        </div>
      </section>
    </main>
  `
})
export class CustomerFeedbackComponent implements OnInit {
  private api = inject(ApiService);
  private route = inject(ActivatedRoute);

  feedback = signal<FeedbackRequest | null>(null);
  isLoading = signal(true);
  isSaving = signal(false);
  submitted = signal(false);
  errorMessage = signal('');
  formError = signal('');
  googleReviewUrl = signal('');
  rating = signal(0);
  ratings = [1, 2, 3, 4, 5];
  serviceCategory = '';
  comments = '';
  testimonialPermission = false;

  alreadySubmitted = computed(() => {
    const status = this.feedback()?.status;
    return ['Submitted', 'Needs Follow-up', 'Resolved', 'Approved Testimonial'].includes(status || '');
  });

  ngOnInit() {
    const token = this.route.snapshot.paramMap.get('token') || '';
    if (!token) {
      this.isLoading.set(false);
      this.errorMessage.set('Feedback link is missing.');
      return;
    }
    this.api.getPublicFeedback(token).subscribe({
      next: (res: ApiResponse<FeedbackLoadData>) => {
        this.isLoading.set(false);
        if (res.success && res.data) {
          this.feedback.set(res.data.feedback);
          this.googleReviewUrl.set(res.data.google_review_url || '');
          this.rating.set(Number(res.data.feedback?.rating || 0));
          this.comments = res.data.feedback?.comments || '';
          this.serviceCategory = res.data.feedback?.service_category || '';
          this.testimonialPermission = !!Number(res.data.feedback?.testimonial_permission || 0);
        } else {
          this.errorMessage.set(res.message || 'Feedback request could not be loaded.');
        }
      },
      error: (err: unknown) => {
        this.isLoading.set(false);
        this.errorMessage.set(this.apiErrorMessage(err, 'Feedback request could not be loaded.'));
      }
    });
  }

  ratingLabel(): string {
    const labels: Record<number, string> = {
      1: 'Poor experience',
      2: 'Below expectations',
      3: 'Okay, but needs improvement',
      4: 'Good service',
      5: 'Excellent service'
    };
    return labels[this.rating()] || 'Choose a rating from 1 to 5.';
  }

  selectRating(value: number) {
    if (this.isSaving() || this.alreadySubmitted()) return;
    this.rating.set(value);
    this.formError.set('');
    if (value >= 4) {
      this.submitFeedback(true);
    }
  }

  submitFeedback(redirectToGoogle = false) {
    const token = this.route.snapshot.paramMap.get('token') || '';
    if (!token || this.isSaving()) return;
    if (this.rating() < 1 || this.rating() > 5) {
      this.formError.set('Please select a rating from 1 to 5.');
      return;
    }
    if (this.rating() <= 3 && this.comments.trim().length < 5) {
      this.formError.set('Please explain what happened so our team can follow up.');
      return;
    }

    this.formError.set('');
    this.isSaving.set(true);
    this.api.submitPublicFeedback({
      token,
      rating: this.rating(),
      comments: this.comments,
      service_category: this.serviceCategory,
      testimonial_permission: this.testimonialPermission
    }).subscribe({
      next: (res: ApiResponse<FeedbackSubmitData>) => {
        this.isSaving.set(false);
        if (res.success && res.data) {
          this.googleReviewUrl.set(res.data?.google_review_url || this.googleReviewUrl());
          if (redirectToGoogle && res.data?.redirect_url) {
            window.location.assign(res.data.redirect_url);
            return;
          }
          this.submitted.set(true);
        } else {
          this.formError.set(res.message || 'Feedback could not be submitted.');
        }
      },
      error: (err: unknown) => {
        this.isSaving.set(false);
        this.formError.set(this.apiErrorMessage(err, 'Feedback could not be submitted.'));
      }
    });
  }

  private apiErrorMessage(error: unknown, fallback: string): string {
    const candidate = error as { error?: { message?: unknown } } | null;
    return typeof candidate?.error?.message === 'string' ? candidate.error.message : fallback;
  }
}
